<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { capFirstChar, short_id } from '../../../helpers/utilities';
	import { load_image_from, real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Alert, Text, Toggle } from '@Shelly/shelly-design-system';
	//Components
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import RangeExtenderClient from './RangeExtenderClient.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { WiFiRequest } from '../../../types/requests/settings/networks/wifi_request';
	import type { Device } from '../../../types/Device';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = $settingsStore;

	let enable = settings?.wifi?.ap?.range_extender?.enable,
		showModal = false;

	$: statusStore = getDeviceStatus(id);
	$: ap_client_count = $statusStore?.wifi?.ap_client_count;

	const connect = async () => {
			let newEnableValue = !enable;
			const params = {
					config: {
						ap: {
							enable: true,
							range_extender: { enable: newEnableValue },
						},
					},
				},
				res: SettingRequest = await gen2Request(device, 'Wifi.SetConfig', params);
			if (res.isok) {
				enable = newEnableValue;
				settings.wifi.ap.range_extender.enable = enable;
				devices_settings.update_devices_settings(id, settings);
			}
			onSettingsResponse(res, device, {
				success: enable
					? $format('range_extender_scc_enabled_toast', {
							default: 'Range extender is enabled.',
						})
					: $format('range_extender_scc_disabled_toast', {
							default: 'Range extender is disabled.',
						}),
			});
		},
		getConnectedDevices = async (update: number | null) => {
			if (update) {
				const res: WiFiRequest = await gen2Request(device, 'WiFi.ListApClients', {});
				if (res.isok) {
					const clients = res?.data?.ap_clients || res?.ap_clients,
						clientsList = { foreignDevices: [], shellyDevices: [] };
					if (clients.length) {
						clients.forEach((client) => {
							const macID = client.mac.replace(/:/g, ''),
								device = $devices[macID] || $devices[short_id(macID)];
							if (device) {
								client.shellyDevice = device;
								clientsList.shellyDevices.push(client);
							} else clientsList.foreignDevices.push(client);
						});
						return clientsList;
					}
				}
			}
		};

	const handleToggleClick = (e) => {
		e.preventDefault();
		showModal = true;
	};
</script>

{#if settings?.zigbee?.enable}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('zigbee_mode_active', {
			default: 'Zigbee Mode Active',
		})}
		isHeadingBold
		message={$format('zigbee_mode_ap_info', {
			default: 'Access Point settings cannot be modified while Zigbee mode is enabled.',
		})}
	/>
{/if}

<Toggle
	size="m"
	bind:checked={enable}
	on:click={handleToggleClick}
	disabled={isOffline || settings?.zigbee?.enable}
	id="toggle-ailop8j"
>
	{capFirstChar(
		$showNewLayout
			? $format('sub_settings_title_range_extender', { default: 'range extender' })
			: $format('enable', {
					default: 'Enable',
				}),
	)}
</Toggle>

{#if ap_client_count && settings?.wifi.ap?.range_extender?.enable}
	{#await getConnectedDevices(ap_client_count) then clientsList}
		{#if clientsList}
			<Text styleType="body-small" class="text-bold mt-s mb-s"
				>{$format('connected_devices', {
					default: 'Connected devices:',
				})}</Text
			>
			{#each clientsList.shellyDevices as client}
				<RangeExtenderClient
					{client}
					{device}
					title={client.shellyDevice.name}
					image={load_image_from(client.shellyDevice.image)}
				/>
			{/each}
			{#if clientsList.foreignDevices.length}
				<Text styleType="body-small" class="text-bold mt-s mb-s"
					>{$format('not_owned_devices', {
						values: { count: clientsList.foreignDevices.length },
						default: 'Foreign devices: {count}',
					})}</Text
				>
				{#each clientsList.foreignDevices as client}
					<RangeExtenderClient
						{client}
						{device}
						title={`IP: ${client.ip}`}
						image="images/device_images/logo_white_S.png"
					/>
				{/each}
			{/if}
		{/if}
	{/await}
{/if}

<NetworkModal
	title={!enable
		? $format('enable_range_extender_title', {
				default: 'Enable Range Extender?',
			})
		: $format('disable_range_extender_title', {
				default: 'Disable Range Extender?',
			})}
	btnTexts={{
		cancelText: '',
		saveText: !enable ? $format('button_enable') : $format('disable'),
	}}
	bind:showModal
	on:confirm={connect}
	{isOffline}
	hideSlotAdditionalInfo={enable || settings?.wifi?.ap?.enable}
>
	{$format('enable_disable_range_extender', {
		values: {
			enable_disable: `${!enable ? $format('label_enable') : $format('disable')}`,
			extend_or_reduce: `${
				!enable
					? $format('will_extend', { default: 'will extend' })
					: $format('may_reduce', { default: 'may reduce' })
			}`,
		},
		default:
			"{enable_disable} the Range Extender {extend_or_reduce} your network's coverage to areas with weaker signal strength.",
	})}

	<div class="mt-m" slot="additionalInfo">
		<Alert
			type="warning"
			heading={$format('enable_range_extender_alert_msg', {
				default: 'Enabling the Range Extender will also enable the Access Point (AP) network.',
			})}
		/>
	</div>
</NetworkModal>
